import type { ReactNode } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import {
  DEFAULT_SPACING_SIZE,
  spacingSize,
} from 'hb-builder/components/BuilderComponents/shared/common/spacingSize';
import Drop from 'hb-builder/components/common/Drop';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import {
  Wrapper as WrapperRender,
  WrapperBuilder,
} from 'hb-builder/components/common/Wrapper';
import { useComponentContext } from 'hb-builder/hooks';
import { getJustifyContent } from 'hb-builder/utils/attributes';
import type { DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import type {
  BUTTONS_SPACING_OPTIONS,
  WrapperItemAttributes,
} from 'hb-react/backoffice/types/builder/itemAttributes/wrapperItemAttributes';

const componentType = 'Wrapper';

const Wrapper = () => <div />; // never used

const WrapperUI = ({
  attributes,
  boxProps,
  children,
  hasMultipleChildren,
}: {
  attributes?: WrapperItemAttributes;
  boxProps?: { [key: string]: any };
  children: ReactNode;
  hasMultipleChildren: boolean;
}) => (
  <ChildrenSpacingWrapper
    $childrenSpacing={attributes?.childrenSpacing || DEFAULT_SPACING_SIZE}
    $isTied={attributes?.tied}
    $hasMultipleChildren={hasMultipleChildren}
    data-testid="builder-component-wrapper"
    flexDirection={attributes?.flexDirection}
    height="100%"
    justifyContent={getJustifyContent(attributes?.horizontalAlign)}
    {...boxProps}
  >
    {children}
  </ChildrenSpacingWrapper>
);
const RenderBuilder = ({ children }) => {
  const { item } = useComponentContext();

  const { attributes, children: childrenItem } = item;

  if (attributes?.tied) {
    return (
      <WrapperUI
        attributes={attributes as WrapperItemAttributes}
        boxProps={{ padding: attributes?.containerPadding, width: '100%' }}
        hasMultipleChildren={(childrenItem?.length || 0) > 1}
      >
        {children}
      </WrapperUI>
    );
  }

  return (
    <Drop accept={['single']}>
      {({ ref: dropRef }) => (
        // @ts-expect-error untyped
        <WrapperBuilder attributes={attributes} ref={dropRef}>
          <WrapperUI
            boxProps={{ maxWidth: '100%' }}
            attributes={attributes as WrapperItemAttributes}
            hasMultipleChildren={(childrenItem?.length || 0) > 1}
          >
            {children}
          </WrapperUI>
        </WrapperBuilder>
      )}
    </Drop>
  );
};

const Render = ({
  children,
  item,
}: RenderProps<DocumentItem<WrapperItemAttributes>>) => {
  const { attributes, children: childrenItem } = item || {};
  const hasMultipleChildren = (childrenItem?.length || 0) > 1;

  return (
    /* @ts-expect-error untyped */
    <WrapperRender attributes={attributes} componentType={componentType}>
      <WrapperUI
        boxProps={{ maxWidth: '100%' }}
        attributes={attributes}
        hasMultipleChildren={hasMultipleChildren}
      >
        {children}
      </WrapperUI>
    </WrapperRender>
  );
};

Wrapper.componentName = 'Wrapper';
Wrapper.componentType = componentType;

Wrapper.canBeDoubleClicked = false;
Wrapper.canBeDragged = false;
Wrapper.canBeListed = false;
Wrapper.canBeSelected = false;

Wrapper.item = ({ attributes = {}, category, children, name, type }) => ({
  attributes,
  category,
  children,
  name,
  type: type || Wrapper.componentName,
});

Wrapper.toolbar = ['edit', 'drag', 'duplicate', 'delete'];
Wrapper.Settings = <div />;

Wrapper.RenderBuilder = RenderBuilder;
Wrapper.Render = Render;

export default Wrapper;

interface ChildrenSpacingWrapperProps {
  $childrenSpacing: BUTTONS_SPACING_OPTIONS;
  $hasMultipleChildren: boolean;
  $isTied: boolean;
}

const ChildrenSpacingWrapper = styled(Box).attrs({
  flexWrap: 'wrap',
})<ChildrenSpacingWrapperProps>`
  ${({ $childrenSpacing, $hasMultipleChildren, $isTied }) =>
    `
    gap: ${spacingSize[$childrenSpacing] || DEFAULT_SPACING_SIZE}px;
    > div {
      width: ${!$isTied && !$hasMultipleChildren ? '' : 'auto !important'};
      max-width: ${
        /* Truncate text btn if only one button */
        !$isTied && !$hasMultipleChildren && '100%'
      };
    }`}
`;
